import type { ComponentPropsWithRef, ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Chip } from "@/components/ui/Chip";
import { cn } from "@/lib/utils";
import {
  segmentedNeutralTrackClass,
  segmentedNeutralTrackSmClass,
  segmentedNeutralTriggerClass,
  segmentedTrackClass,
  segmentedTriggerClass,
} from "./segmented-variants";

export type ToggleGroupOption<T extends string> = {
  value: T;
  label: ReactNode;
  icon?: LucideIcon;
  disabled?: boolean;
};

export type ToggleGroupVariant = "chips" | "segmented" | "segmented-neutral";

export type ToggleGroupProps<T extends string> = Omit<
  ComponentPropsWithRef<"div">,
  "children" | "onChange"
> & {
  value: T;
  onChange: (value: T) => void;
  options: ToggleGroupOption<T>[];
  variant?: ToggleGroupVariant;
  size?: "sm" | "md";
  ariaLabel: string;
};

export function ToggleGroup<T extends string>({
  value,
  onChange,
  options,
  variant = "chips",
  size = "md",
  ariaLabel,
  className,
  ...rest
}: ToggleGroupProps<T>) {
  if (variant === "segmented-neutral") {
    return (
      <div
        className={cn(
          size === "sm" ? segmentedNeutralTrackSmClass : segmentedNeutralTrackClass,
          className,
        )}
        role="group"
        aria-label={ariaLabel}
        {...rest}
      >
        {options.map((option) => {
          const Icon = option.icon;
          const selected = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              disabled={option.disabled}
              aria-pressed={selected}
              onClick={() => onChange(option.value)}
              className={segmentedNeutralTriggerClass(selected, size)}
            >
              {Icon ? <Icon className="h-4 w-4 shrink-0" aria-hidden /> : null}
              {option.label}
            </button>
          );
        })}
      </div>
    );
  }

  if (variant === "segmented") {
    return (
      <div
        className={cn("inline-flex", segmentedTrackClass, className)}
        role="group"
        aria-label={ariaLabel}
        {...rest}
      >
        {options.map((option) => {
          const Icon = option.icon;
          const selected = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              disabled={option.disabled}
              aria-pressed={selected}
              onClick={() => onChange(option.value)}
              className={segmentedTriggerClass(selected, size)}
            >
              {Icon ? <Icon className="h-4 w-4 shrink-0" aria-hidden /> : null}
              {option.label}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className={cn("flex flex-wrap items-center gap-2", className)}
      role="radiogroup"
      aria-label={ariaLabel}
      {...rest}
    >
      {options.map((option) => {
        const Icon = option.icon;
        const selected = option.value === value;
        return (
          <Chip
            key={option.value}
            size={size}
            selected={selected}
            disabled={option.disabled}
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.value)}
          >
            {Icon ? (
              <Icon
                className={size === "sm" ? "h-3 w-3 shrink-0" : "h-3.5 w-3.5 shrink-0"}
                aria-hidden
              />
            ) : null}
            {option.label}
          </Chip>
        );
      })}
    </div>
  );
}
